import React from 'react';
import { FileText, Download, Eye } from 'lucide-react';
import { getMediaUrl } from '../utils/media';

/**
 * Phase document row: doc icon, bold filename, divider, download (guidelines) or eye (BOQ).
 * @param {boolean} disabled — locked (e.g. not subscribed); opens subscribe flow via onLockedClick
 */
export function PhaseDocumentFileRow({
  doc,
  docIndex,
  defaultLabel = 'Document',
  rightAction = 'download',
  compact = false,
  disabled = false,
  onLockedClick,
}) {
  const docUrl = getMediaUrl(doc);
  const docName = doc.name || doc.key?.split('/').pop() || `${defaultLabel} ${docIndex + 1}`;
  const hasUrl = Boolean(docUrl && String(docUrl).trim() !== '' && docUrl !== '#');
  const canOpen = !disabled && hasUrl;
  const isView = rightAction === 'view';
  const fileIconSize = compact ? 17 : 19;
  const actionIconSize = compact ? 17 : 19;
  const padMain = compact ? 'px-2.5 py-2.5 gap-2.5' : 'px-3 py-3 gap-3';
  const padAction = compact ? 'min-w-[40px] px-2' : 'min-w-[44px] px-3';
  const nameClass = compact ? 'text-xs font-semibold' : 'text-sm font-semibold';

  const rowClass = `flex items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-colors ${
    disabled ? 'opacity-60' : 'hover:border-gray-300'
  }`;

  const mainClass = `flex min-w-0 flex-1 items-center text-left ${padMain} ${nameClass} ${
    canOpen ? 'text-gray-900 cursor-pointer' : 'text-gray-500 cursor-not-allowed'
  }`;

  const actionClass = `inline-flex shrink-0 items-center justify-center self-stretch border-l border-gray-200 bg-white text-gray-600 ${padAction} ${
    canOpen ? 'transition-colors hover:bg-gray-50 hover:text-gray-900' : 'cursor-not-allowed'
  }`;

  const icon = isView ? (
    <Eye size={actionIconSize} strokeWidth={2} className="text-gray-700" aria-hidden />
  ) : (
    <Download size={actionIconSize} strokeWidth={2} className="text-gray-700" aria-hidden />
  );

  if (disabled) {
    return (
      <li>
        <div className={rowClass}>
          <button type="button" className={mainClass} onClick={onLockedClick}>
            <FileText className="shrink-0 text-gray-500" size={fileIconSize} strokeWidth={1.75} aria-hidden />
            <span className="min-w-0 flex-1 break-words leading-snug text-left">{docName}</span>
          </button>
          <button
            type="button"
            className={actionClass}
            onClick={onLockedClick}
            aria-label={isView ? `View ${docName}` : `Download ${docName}`}
            title={isView ? 'View' : 'Download'}
          >
            {icon}
          </button>
        </div>
      </li>
    );
  }

  return (
    <li>
      <div className={rowClass}>
        <a
          href={hasUrl ? docUrl : undefined}
          target={hasUrl ? '_blank' : undefined}
          rel={hasUrl ? 'noopener noreferrer' : undefined}
          className={mainClass}
          onClick={(e) => {
            if (!hasUrl) e.preventDefault();
          }}
        >
          <FileText className="shrink-0 text-gray-500" size={fileIconSize} strokeWidth={1.75} aria-hidden />
          <span className="min-w-0 flex-1 break-words leading-snug">{docName}</span>
        </a>
        {hasUrl ? (
          <a
            href={docUrl}
            {...(isView ? {} : { download: docName })}
            target="_blank"
            rel="noopener noreferrer"
            className={actionClass}
            aria-label={isView ? `View ${docName}` : `Download ${docName}`}
            title={isView ? 'View' : 'Download'}
          >
            {icon}
          </a>
        ) : null}
      </div>
    </li>
  );
}

/** Alias for BV phase page */
export const PhaseMediaFileRow = PhaseDocumentFileRow;
